import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import AccessPending from "@/pages/AccessPending";
import ResetPassword from "@/pages/ResetPassword";
import CrewApp from "@/pages/crew/CrewApp";
import ForemanApp from "@/pages/foreman/ForemanApp";
import AdminApp from "@/pages/admin/AdminApp";
import NotificationsPage from "@/pages/NotificationsPage";
import { BACKEND_URL_MISSING } from "@/lib/api";
import ErrorBoundary from "@/components/ErrorBoundary";
import PracticeBanner from "@/components/PracticeBanner";

// A build with no REACT_APP_BACKEND_URL cannot talk to anything. Rendering the
// login screen anyway produces a button that blinks and does nothing, with the
// real cause invisible. Say what is wrong instead -- whoever sees this can fix
// it, and nobody wastes an afternoon on a login that was never going to work.
function BackendNotConfigured() {
    return (
        <div className="min-h-screen paper-bg flex items-center justify-center p-6" data-testid="backend-not-configured">
            <div className="max-w-md bg-white border-2 border-brand-danger/40 rounded-2xl shadow-card p-6 space-y-3">
                <div className="text-[10px] uppercase tracking-widest text-brand-danger font-bold">
                    This build is not configured
                </div>
                <h1 className="font-bold text-xl text-brand-charcoal">The app has no backend address</h1>
                <p className="text-sm text-brand-charcoal/80">
                    <code className="font-mono text-xs bg-brand-stone px-1.5 py-0.5 rounded">REACT_APP_BACKEND_URL</code>{" "}
                    was not set when this version was built, so it cannot reach the server and sign-in cannot work.
                </p>
                <p className="text-sm text-brand-charcoal/80">
                    On Cloudflare Pages, Preview and Production keep{" "}
                    <strong>separate</strong> environment variables — a preview build needs its own copy.
                    Set it, then redeploy this branch.
                </p>
            </div>
        </div>
    );
}

function homeForRole(role) {
    if (role === "admin") return "/admin";
    if (role === "manager") return "/foreman";
    return "/crew";
}

function ProtectedRoute({ children, role }) {
    const { loading, user, employee } = useAuth();
    if (loading) {
        return <div className="min-h-screen flex items-center justify-center paper-bg text-brand-charcoal/60" data-testid="checking-auth">Checking…</div>;
    }
    if (!user) return <Navigate to="/login" replace />;
    if (!employee) return <Navigate to="/access-pending" replace />;
    if (role && employee.role !== role) {
        return <Navigate to={homeForRole(employee.role)} replace />;
    }
    return children;
}

function RootRouter() {
    const location = useLocation();
    // Handle OAuth callback hash synchronously during render
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/access-pending" element={<AccessPending />} />

            <Route path="/crew/*" element={<ProtectedRoute role="worker"><CrewApp /></ProtectedRoute>} />
            <Route path="/foreman/*" element={<ProtectedRoute role="manager"><ForemanApp /></ProtectedRoute>} />
            <Route path="/admin/*" element={<ProtectedRoute role="admin"><AdminApp /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />

            <Route path="/" element={<RoleRedirect />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function RoleRedirect() {
    const { loading, user, employee } = useAuth();
    if (loading) return <div className="min-h-screen flex items-center justify-center paper-bg text-brand-charcoal/60">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (!employee) return <Navigate to="/access-pending" replace />;
    return <Navigate to={homeForRole(employee.role)} replace />;
}

export default function App() {
    if (BACKEND_URL_MISSING) return <BackendNotConfigured />;
    return (
        <BrowserRouter>
            {/* Outside the error boundary on purpose: if the app crashes, the
                one thing that must still be true on screen is which environment
                you are in. */}
            <PracticeBanner />
            {/* One Toaster for the whole app. Every page's toast.success/error
                calls surface through this single instance instead of each
                page mounting its own -- four divergently-styled copies used
                to exist, which is how a foreman screen's toast looked
                different from a crew screen's for no reason. */}
            <Toaster position="top-center" />
            {/* Inside the router so the fallback can still be navigated away from,
                and outside AuthProvider so a failure there is caught too. */}
            <ErrorBoundary>
                <AuthProvider>
                    <RootRouter />
                </AuthProvider>
            </ErrorBoundary>
        </BrowserRouter>
    );
}
